"use client";

import React, { useEffect, useState } from "react";
import Kicker from "@/src/components/common/broadsheet/kicker";
import { FONT_UI } from "@/src/components/common/broadsheet/tokens";
import { Box, Link, List, ListItem, Typography } from "@mui/material";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";

const ArticleOutlines = ({
  hasAbstract = false,
  hasAcknowledgments = false,
  hasFootnotes = false,
  hasReferences = false,
  hasCopyright = false,
}: {
  hasAbstract?: boolean;
  hasAcknowledgments?: boolean;
  hasFootnotes?: boolean;
  hasReferences?: boolean;
  hasCopyright?: boolean;
}) => {
  const [headings, setHeadings] = useState<{ id: string; label: string }[]>([]);

  useEffect(() => {
    const articleBody = document.getElementById("article-body");
    if (articleBody) {
      const headingElements = articleBody.querySelectorAll("h2");
      const extractedHeadings = Array.from(headingElements).map(
        (heading: any, index: number) => {
          heading.id = `section-${index}`;
          const id = `section-${index}`;
          return { id, label: heading.textContent || "" };
        },
      );
      setHeadings(extractedHeadings);
    }
  }, []);

  const handleScrollToSection = (
    event: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    event.preventDefault();
    const section = document.querySelector(`#${sectionId}`);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const listStyle = {
    m: 0,
    p: 0,
    "& .MuiListItem-root": {
      display: "block",
      p: 0,
      "& a": {
        display: "block",
        fontFamily: FONT_UI,
        fontSize: "0.75rem",
        lineHeight: 1.4,
        color: "custom.inkMuted",
        textDecoration: "none",
        borderLeft: "2px solid",
        borderColor: "custom.ruleHairline",
        pl: "0.625rem",
        paddingBlock: "0.4375rem",
        "&:hover": {
          color: "custom.link",
          borderColor: "custom.link",
        },
      },
      "& span": {
        fontFamily: "inherit",
        fontSize: "inherit",
        color: "inherit",
      },
    },
  };

  return (
    <Box
      sx={{ width: "100%" }}>
      <Kicker component="h2" sx={{ mb: 0.375 }}>
        Outline
      </Kicker>
      <List sx={listStyle}>
        {hasAbstract && (
          <ListItem>
            <Link
              href="#article-abstract"
              style={{ display: "flex", alignItems: "baseline" }}
              onClick={(event) => {
                handleScrollToSection(event, "article-abstract");
              }}
            >
              <SpanTypography>Abstract</SpanTypography>
            </Link>
          </ListItem>
        )}
        {headings.map((heading, index) => (
          <ListItem key={`outline-${index}`}>
            <Link
              href={`#${heading.id}`}
              onClick={(event) => handleScrollToSection(event, heading.id)}
            >
              <Typography>{heading.label}</Typography>
            </Link>
          </ListItem>
        ))}
        {hasAcknowledgments && (
          <ListItem>
            <Link
              href="#article-acknowledgments"
              onClick={(event) =>
                handleScrollToSection(event, "article-acknowledgments")
              }
            >
              <SpanTypography>Acknowledgments</SpanTypography>
            </Link>
          </ListItem>
        )}
        {hasFootnotes && (
          <ListItem>
            <Link
              href="#article-footnotes"
              onClick={(event) =>
                handleScrollToSection(event, "article-footnotes")
              }
            >
              <SpanTypography>Footnotes</SpanTypography>
            </Link>
          </ListItem>
        )}
        {hasReferences && (
          <ListItem>
            <Link
              href="#article-references"
              onClick={(event) =>
                handleScrollToSection(event, "article-references")
              }
            >
              <SpanTypography>References</SpanTypography>
            </Link>
          </ListItem>
        )}
        {hasCopyright && (
          <ListItem>
            <Link
              href="#article-copyright"
              onClick={(event) =>
                handleScrollToSection(event, "article-copyright")
              }
            >
              <SpanTypography>Copyright</SpanTypography>
            </Link>
          </ListItem>
        )}
      </List>
    </Box>
  );
};

export default ArticleOutlines;
